import { useEffect, useMemo, useRef, useState } from 'react'

import { type IntegrationServiceId } from '../../../../common/integrations'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientClients } from '../../../../store/crossClient/crossClientResults'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'

import { useFetchMissingCrossClientClients } from '../../Results/hooks'

export interface CrmEligibleEntity {
  clientId: string
  externalId: string
  ownerId: string | undefined
}

export type CrmEligibility =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready'
      totalCount: number
      eligibleCount: number
      skippedCount: number
      eligibleTargets: CrmEligibleEntity[]
    }

export const useCrmEligibility = (serviceId: IntegrationServiceId, results: CrossClientQueryResult[]): CrmEligibility => {
  const clients = useAppSelector(selectCrossClientClients)
  const fetchMissingClients = useFetchMissingCrossClientClients()
  const fetchRef = useRef(fetchMissingClients)
  useEffect(() => {
    fetchRef.current = fetchMissingClients
  }, [fetchMissingClients])

  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>(() => (results.length ? 'loading' : 'ready'))

  useEffect(() => {
    if (!results.length) {
      return
    }
    let cancelled = false

    fetchRef
      .current(results.map((result) => result.clientId))
      .then(() => {
        if (!cancelled) {
          setStatus('ready')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error')
        }
      })

    return () => {
      cancelled = true
    }
  }, [results])

  const eligibleTargets = useMemo<CrmEligibleEntity[]>(() => {
    if (status !== 'ready') {
      return []
    }
    return results.reduce<CrmEligibleEntity[]>((acc, result) => {
      const externalId = clients[result.clientId]?.integrations?.find((i) => i.serviceId === serviceId)?.externalId
      const ownerId = clients[result.clientId]?.owner?.ownerId
      if (externalId) {
        acc.push({ clientId: result.clientId, externalId, ownerId })
      }
      return acc
    }, [])
  }, [status, results, clients, serviceId])

  if (status === 'loading') {
    return { status: 'loading' }
  }
  if (status === 'error') {
    return { status: 'error' }
  }

  const totalCount = results.length
  const eligibleCount = eligibleTargets.length
  return {
    status: 'ready',
    totalCount,
    eligibleCount,
    skippedCount: totalCount - eligibleCount,
    eligibleTargets,
  }
}
